/**
 * MainModulePageSkeleton — Esqueleto genérico para módulos del layout principal
 * (tablas, listas, dashboards con tarjetas).
 */
import {
  Skeleton,
  SkeletonMetricCard,
  SkeletonTableRows,
} from "@/components/ui";

type MainModulePageSkeletonKind = "table" | "metrics" | "cards";

export function MainModulePageSkeleton({
  kind = "table",
}: {
  kind?: MainModulePageSkeletonKind;
}) {
  return (
    <div
      className="p-3 sm:p-4 lg:p-6 space-y-4 animate-fadeIn min-h-[50vh]"
      role="status"
      aria-busy="true"
      aria-label="Cargando página"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="space-y-2 min-w-0 flex-1">
          <Skeleton width="w-40 sm:w-56" height="h-7" rounded="md" />
          <Skeleton width="w-full max-w-md" height="h-4" rounded="md" />
        </div>
        <div className="flex gap-2 shrink-0">
          <Skeleton width="w-24" height="h-9" rounded="lg" />
          <Skeleton width="w-28" height="h-9" rounded="lg" />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <Skeleton width="w-32" height="h-9" rounded="lg" />
        <Skeleton width="w-40" height="h-9" rounded="lg" />
        <Skeleton width="w-24" height="h-9" rounded="lg" />
      </div>

      {kind === "metrics" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonMetricCard key={i} />
          ))}
        </div>
      ) : null}

      {kind === "cards" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 space-y-3"
            >
              <Skeleton width="w-3/4" height="h-5" rounded="md" />
              <Skeleton width="w-full" height="h-4" rounded="md" />
              <Skeleton width="w-1/2" height="h-4" rounded="md" />
            </div>
          ))}
        </div>
      ) : null}

      {kind === "table" ? (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-slate-700 flex gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} width="w-20" height="h-4" rounded="md" />
            ))}
          </div>
          <SkeletonTableRows rows={8} cols={4} />
        </div>
      ) : null}
    </div>
  );
}
